import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const PrivacyPolicyPage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen py-8 px-4" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto space-y-8">

        {/* Header */}
        <div className="flex items-center gap-3">
          <Link to="/" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-cyan-400" />
              <h1 className="text-2xl sm:text-3xl font-bold text-white">{t('privacy.title')}</h1>
            </div>
            <p className="text-sm text-gray-400 mt-1">R.E.Mobiles — {t('privacy.last_updated')}</p>
          </div>
        </div>

        {/* Intro */}
        <div className="glass p-6 text-sm text-gray-300 leading-relaxed">
          {t('privacy.intro.part1')}<strong className="text-white">R.E.Mobiles</strong>{t('privacy.intro.part2')}<strong className="text-cyan-400">{t('privacy.intro.rgpd')}</strong>{t('privacy.intro.part3')}
        </div>

        {/* Section 1 */}
        <Section num="1" title={t('privacy.s1.title')}>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoRow label={t('privacy.s1.company_label')} value="Resurrection Mobile (R.E.Mobiles)" />
              <InfoRow label={t('privacy.s1.status_label')} value="SASU" />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 text-sm">
              <a href="mailto:contact@remobiles.com" className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
                <Mail className="w-4 h-4" /> contact@remobiles.com
              </a>
              <a href="mailto:infos@guyarepair.com" className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
                <Mail className="w-4 h-4" /> infos@guyarepair.com
              </a>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 text-sm text-gray-300">
              <span className="flex items-center gap-2"><Phone className="w-4 h-4 text-green-400" /> 0694 27 40 51</span>
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-purple-400" /> 102 Avenue General de Gaulle, Porte 2, Guyane française</span>
            </div>
            <p className="text-sm text-gray-400 mt-2">{t('privacy.s1.controller_note')}</p>
          </div>
        </Section>

        {/* Section 2 */}
        <Section num="2" title={t('privacy.s2.title')}>
          <SubSection title={t('privacy.s2.sub1_title')}>
            <BulletList items={[t('privacy.s2.sub1_b1'), t('privacy.s2.sub1_b2'), t('privacy.s2.sub1_b3'), t('privacy.s2.sub1_b4'), t('privacy.s2.sub1_b5')]} />
          </SubSection>
          <SubSection title={t('privacy.s2.sub2_title')}>
            <BulletList items={[t('privacy.s2.sub2_b1'), t('privacy.s2.sub2_b2'), t('privacy.s2.sub2_b3'), t('privacy.s2.sub2_b4')]} />
          </SubSection>
          <SubSection title={t('privacy.s2.sub3_title')}>
            <BulletList items={[t('privacy.s2.sub3_b1'), t('privacy.s2.sub3_b2')]} />
            <div className="mt-3 p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-sm">
              <strong className="text-green-400">{t('privacy.s2.payment_important_label')}</strong> <span className="text-gray-300">{t('privacy.s2.payment_important_text')}</span>
            </div>
          </SubSection>
          <SubSection title={t('privacy.s2.sub4_title')}>
            <BulletList items={[t('privacy.s2.sub4_b1'), t('privacy.s2.sub4_b2'), t('privacy.s2.sub4_b3'), t('privacy.s2.sub4_b4')]} />
          </SubSection>
        </Section>

        {/* Section 3 */}
        <Section num="3" title={t('privacy.s3.title')}>
          <BulletList items={[
            t('privacy.s3.b1'),
            t('privacy.s3.b2'),
            t('privacy.s3.b3'),
            t('privacy.s3.b4'),
            t('privacy.s3.b5'),
            t('privacy.s3.b6'),
            t('privacy.s3.b7')
          ]} />
        </Section>

        {/* Section 4 */}
        <Section num="4" title={t('privacy.s4.title')}>
          <p className="text-sm text-gray-400 mb-3">{t('privacy.s4.intro')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[t('privacy.s4.i1'), t('privacy.s4.i2'), t('privacy.s4.i3'), t('privacy.s4.i4')].map((item, i) => (
              <div key={i} className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300">
                {item}
              </div>
            ))}
          </div>
        </Section>

        {/* Section 5 */}
        <Section num="5" title={t('privacy.s5.title')}>
          <BulletList items={[
            t('privacy.s5.b1'),
            t('privacy.s5.b2'),
            t('privacy.s5.b3')
          ]} />
          <div className="mt-3 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-sm text-blue-300 font-medium">
            {t('privacy.s5.no_sale')}
          </div>
        </Section>

        {/* Section 6 */}
        <Section num="6" title={t('privacy.s6.title')}>
          <p className="text-sm text-gray-300 leading-relaxed">{t('privacy.s6.text')}</p>
        </Section>

        {/* Section 7 */}
        <Section num="7" title={t('privacy.s7.title')}>
          <div className="space-y-2">
            {[
              { label: t('privacy.s7.r1_label'), value: t('privacy.s7.r1_value') },
              { label: t('privacy.s7.r2_label'), value: t('privacy.s7.r2_value') },
              { label: t('privacy.s7.r3_label'), value: t('privacy.s7.r3_value') },
              { label: t('privacy.s7.r4_label'), value: t('privacy.s7.r4_value') },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5 text-sm">
                <span className="text-gray-400">{item.label}</span>
                <span className="text-white font-medium text-right">{item.value}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Section 8 */}
        <Section num="8" title={t('privacy.s8.title')}>
          <p className="text-sm text-gray-400 mb-3">{t('privacy.s8.intro')}</p>
          <BulletList items={[
            t('privacy.s8.b1'),
            t('privacy.s8.b2'),
            t('privacy.s8.b3'),
            t('privacy.s8.b4'),
            t('privacy.s8.b5')
          ]} />
        </Section>

        {/* Section 9 */}
        <Section num="9" title={t('privacy.s9.title')} id="suppression">
          <p className="text-sm text-gray-400 mb-3">{t('privacy.s9.intro')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            {[t('privacy.s9.r1'), t('privacy.s9.r2'), t('privacy.s9.r3'), t('privacy.s9.r4'), t('privacy.s9.r5'), t('privacy.s9.r6'), t('privacy.s9.r7')].map((right, i) => (
              <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-cyan-500/5 border border-cyan-500/10 text-sm text-gray-300">
                <Shield className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                {right}
              </div>
            ))}
          </div>
          <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-300 space-y-2">
            <p>{t('privacy.s9.exercise')} <a href="mailto:contact@remobiles.com" className="text-cyan-400 hover:underline">contact@remobiles.com</a></p>
            <p>{t('privacy.s9.complaint_before')}<a href="https://www.cnil.fr" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline inline-flex items-center gap-1">CNIL <ExternalLink className="w-3 h-3" /></a>{t('privacy.s9.complaint_after')}</p>
          </div>
        </Section>

        {/* Section 10 */}
        <Section num="10" title={t('privacy.s10.title')}>
          <p className="text-sm text-gray-400 mb-3">{t('privacy.s10.intro')}</p>
          <BulletList items={[
            t('privacy.s10.b1'),
            t('privacy.s10.b2'),
            t('privacy.s10.b3')
          ]} />
          <p className="text-sm text-gray-400 mt-3">{t('privacy.s10.manage')}</p>
        </Section>

        {/* Section 11 */}
        <Section num="11" title={t('privacy.s11.title')}>
          <p className="text-sm text-gray-300 leading-relaxed">{t('privacy.s11.text')}</p>
        </Section>

        {/* Section 12 */}
        <Section num="12" title={t('privacy.s12.title')}>
          <p className="text-sm text-gray-300 leading-relaxed">{t('privacy.s12.text')}</p>
        </Section>

        {/* Section 13 */}
        <Section num="13" title={t('privacy.s13.title')}>
          <div className="space-y-3 text-sm">
            <p className="text-gray-300">{t('privacy.s13.intro')}</p>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
              <div className="font-bold text-white">Resurrection Mobile</div>
              <a href="mailto:contact@remobiles.com" className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
                <Mail className="w-4 h-4" /> contact@remobiles.com
              </a>
              <a href="mailto:infos@guyarepair.com" className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
                <Mail className="w-4 h-4" /> infos@guyarepair.com
              </a>
              <div className="flex items-center gap-2 text-gray-300">
                <Phone className="w-4 h-4 text-green-400" /> 0694 27 40 51
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <MapPin className="w-4 h-4 text-purple-400" /> 102 Avenue General de Gaulle, Porte 2, Guyane française
              </div>
            </div>
          </div>
        </Section>

        {/* Footer */}
        <div className="glass p-5 text-center text-sm text-gray-400 border-t border-cyan-500/20">
          <strong className="text-gray-300">{t('privacy.footer')}</strong>
        </div>

      </div>
    </div>
  );
};

const Section = ({ num, title, id, children }) => (
  <div className="glass p-5 sm:p-6" id={id} data-testid={`privacy-section-${num}`}>
    <h2 className="text-base sm:text-lg font-bold text-white mb-4 flex items-center gap-2">
      <span className="w-7 h-7 rounded-lg bg-cyan-500/20 text-cyan-400 text-xs font-bold flex items-center justify-center shrink-0">{num}</span>
      {title}
    </h2>
    {children}
  </div>
);

const SubSection = ({ title, children }) => (
  <div className="mb-4 last:mb-0">
    <h3 className="text-sm font-bold text-gray-300 mb-2">{title}</h3>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 shrink-0"></span>
        {item}
      </li>
    ))}
  </ul>
);

const InfoRow = ({ label, value }) => (
  <div className="p-2.5 rounded-lg bg-white/5 text-sm">
    <span className="text-gray-500 text-xs">{label}</span>
    <div className="text-white font-medium">{value}</div>
  </div>
);

export default PrivacyPolicyPage;
